import { enumeration, type GameState, type PuzzleModel } from '@arrow/game-engine';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';

import { clueText } from '../game/view.ts';

interface ClueListProps {
	model: PuzzleModel;
	game: GameState;
	/** The answer to mark as where you are: the cursor's while playing, the one read back after. */
	activeId: string | null;
	/** A row picks its clue, as a tap on its clue cell does. */
	onPick: (clueId: string) => void;
}

const noFocusSteal = (e: React.PointerEvent) => e.preventDefault();

/**
 * The wide game's clue list (J3, docs/mockups/audit-decisions.html): every clue, the ones still
 * to solve first in the order Tab takes them, then the solved ones with their answers, quieter.
 * The selected clue is marked as the board marks it (bolder, a vermilion rule). A row picks
 * its clue with the mouse; from the keyboard, Tab in the grid does the same, so the rows
 * aren't Tab stops of their own.
 */
export const ClueList = memo(function ClueList({ model, game, activeId, onPick }: ClueListProps) {
	const solved = useMemo(() => new Set(game.solvedAnswers), [game.solvedAnswers]);
	const order = useMemo(
		() => [...model.answerOrder.filter((id) => !solved.has(id)), ...model.answerOrder.filter((id) => solved.has(id))],
		[model, solved],
	);
	const listRef = useRef<HTMLOListElement>(null);
	// Keep the selected row in view (the list scrolls on its own; the page never does).
	useLayoutEffect(() => {
		const list = listRef.current;
		const row = activeId ? list?.querySelector<HTMLElement>('[aria-current]') : null;
		if (!list || !row) return;
		const top = row.offsetTop; // .clist-rows is the rows' offset parent (it's positioned)
		const bottom = top + row.offsetHeight;
		if (top < list.scrollTop) list.scrollTop = top;
		else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
	}, [activeId, order]);

	return (
		<section className="clist" aria-labelledby="clist-h">
			{/* A list of clues beside the board says what it is: the name is for screen readers. */}
			<h2 id="clist-h" className="sr-only">
				Clues
			</h2>
			{/* The list scrolls on its own, so it's a Tab stop of its own (the arrow keys scroll it),
			    named by its heading; its rows are the mouse's. */}
			<ol ref={listRef} className="clist-rows" tabIndex={0} aria-labelledby="clist-h">
				{order.map((id) => {
					const answer = model.answers[id];
					if (!answer) return null;
					const done = solved.has(id);
					const on = id === activeId;
					const letters = answer.cells.map((c) => game.entries[c] || '').join('');
					return (
						<li key={id}>
							<button
								type="button"
								className="clist-row"
								data-answer={id}
								data-done={done ? '' : undefined}
								aria-current={on ? 'true' : undefined}
								tabIndex={-1}
								onPointerDown={noFocusSteal}
								onClick={() => onPick(answer.clueId)}
							>
								<span className="clist-t">{clueText(model, id)}</span>
								{done ? (
									<span className="clist-ans">
										<span className="sr-only">, solved: </span>
										{letters}
									</span>
								) : (
									<span className="clist-len">
										<span aria-hidden="true">({enumeration(answer).label})</span>
										<span className="sr-only">, {enumeration(answer).spoken}</span>
									</span>
								)}
							</button>
						</li>
					);
				})}
			</ol>
		</section>
	);
});

/** The keyboard, drawn small: Show keys / Hide keys. */
export function KeysIcon() {
	return (
		<svg className="keys-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="2.5" y="6" width="19" height="12" rx="2.5" />
			<path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M7 14h10" />
		</svg>
	);
}
